import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { buildSidebarNavigationItems } from '../../lib/accessControlRegistry';
import { useAuth } from '../../contexts/AuthContext';

const permitsIntersect = (permissionsAny, codeSet) => {
  const list = Array.isArray(permissionsAny) ? permissionsAny.filter((p) => typeof p === 'string' && p.trim()) : [];
  if (!list.length) return false;
  return list.some((p) => codeSet.has(p.trim()));
};

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { permissionCodes: ctxPermissionCodes } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  // ── Get user roles from localStorage (set during login) ──────────────────
  const getUserRoles = () => {
    try {
      const stored = localStorage.getItem('authUser');
      if (!stored) return [];
      const authUser = JSON.parse(stored);
      return authUser.roleTypes
        || authUser.roles?.map(r => r.role_type)
        || (authUser.primaryRole ? [authUser.primaryRole] : [])
        || [];
    } catch (e) {
      console.error('Failed to parse user roles:', e);
      return [];
    }
  };

  const userRoles = getUserRoles();
  const isAdministrator = userRoles.includes('administrator');
  const isDentistOnlyClient =
    userRoles.includes('dentist_client') && !isAdministrator;

  const hasRequiredRole = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!userRoles || userRoles.length === 0) return false;
    return requiredRoles.some(role => userRoles.includes(role));
  };

  const permissionCodeSet = useMemo(() => {
    let list =
      Array.isArray(ctxPermissionCodes) && ctxPermissionCodes.length > 0
        ? ctxPermissionCodes
        : [];
    if (!list.length) {
      try {
        const raw = localStorage.getItem('authUser');
        const u = raw ? JSON.parse(raw) : null;
        if (Array.isArray(u?.permissionCodes)) list = u.permissionCodes;
      } catch (_) {
        list = [];
      }
    }
    return new Set(
      list.filter((c) => typeof c === 'string' && c.trim()).map((c) => c.trim()),
    );
  }, [ctxPermissionCodes, userRoles]);

  const navigationItems = useMemo(
    () => buildSidebarNavigationItems({ t, isDentistOnlyClient }),
    [t, isDentistOnlyClient],
  );

  const itemPassesAccess = useMemo(() => {
    const roleGate = (item, parentRolesGate) =>
      Array.isArray(item.roles) && item.roles.length > 0
        ? item.roles
        : Array.isArray(parentRolesGate)
          ? parentRolesGate
          : [];

    return (item, inheritedPermissionsAny, parentRolesGate) => {
      const effectivePerms =
        Array.isArray(item.permissionsAny) && item.permissionsAny.length > 0
          ? item.permissionsAny
          : inheritedPermissionsAny;
      const viaPerm =
        Array.isArray(effectivePerms) &&
        effectivePerms.length > 0 &&
        permitsIntersect(effectivePerms, permissionCodeSet);
      const viaRole = hasRequiredRole(roleGate(item, parentRolesGate));
      const explicitPermCodes = Array.isArray(item.permissionsAny) && item.permissionsAny.length > 0;
      if (explicitPermCodes) {
        if (isAdministrator && hasRequiredRole(roleGate(item, parentRolesGate))) {
          return true;
        }
        return viaPerm;
      }
      return viaRole;
    };
  }, [permissionCodeSet, userRoles, isAdministrator]);

  const filterNavigationItems = (items, inheritedPermissionsAny = undefined, parentRolesGate = undefined) =>
    items
      .map((item) => {
        const nextInheritedPerms =
          Array.isArray(item.permissionsAny) && item.permissionsAny.length > 0
            ? item.permissionsAny
            : inheritedPermissionsAny;
        const nextInheritedRoles =
          Array.isArray(item.roles) && item.roles.length > 0 ? item.roles : parentRolesGate;

        if (!itemPassesAccess(item, inheritedPermissionsAny, parentRolesGate)) return null;
        if (item.children) {
          const filteredChildren = item.children
            .map((child) => {
              if (!itemPassesAccess(child, nextInheritedPerms, nextInheritedRoles)) return null;
              return child;
            })
            .filter(Boolean);
          if (filteredChildren.length === 0 && !item.path) return null;
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter(Boolean);

  const visibleNavigationItems = useMemo(
    () => filterNavigationItems(navigationItems),
    [navigationItems, itemPassesAccess],
  );

  const pathname = location?.pathname || '';

  const isGroupExpanded = (item) => {
    const key = item.label + (item.path || '');
    if (expandedGroups.has(key)) return true;
    const hasActiveChild = item.children?.some(
      c => c.path === pathname || c.children?.some(sc => sc.path === pathname)
    );
    return !!hasActiveChild;
  };

  const toggleGroup = (item) => {
    const key = item.label + (item.path || '');
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    visibleNavigationItems.forEach(item => {
      if (!item.children?.length) return;
      const hasActive = item.children.some(
        c => c.path === pathname || c.children?.some(sc => sc.path === pathname)
      );
      if (hasActive) {
        const key = item.label + (item.path || '');
        setExpandedGroups(prev => new Set(prev).add(key));
      }
    });
  }, [pathname]);

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      setIsMobileOpen(false);
    }
  };

  const isActive = (path) => pathname === path;

  const hasActiveChild = (item) => {
    if (!item.children) return false;
    return item.children.some(
      c => c.path === pathname || (c.children && c.children.some(sc => sc.path === pathname))
    );
  };

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileOpen]);

  const renderNavItem = (item, depth = 0) => {
    const active = isActive(item?.path);
    const paddingLeft = (depth * 16) + (isCollapsed ? 0 : 16);
    const itemTitle = isCollapsed ? item?.label : undefined;

    if (item?.children && item.children.length > 0) {
      const expanded = isGroupExpanded(item);
      const parentHasActive = hasActiveChild(item);

      return (
        <div key={item?.label} className="sidebar-nav-group">
          <div
            className={`sidebar-nav-item sidebar-nav-group-header ${parentHasActive ? 'sidebar-nav-item-active-child' : ''}`}
            style={{ paddingLeft }}
            onClick={() => toggleGroup(item)}
            title={itemTitle}
          >
            <Icon name={item?.icon} size={20} />
            {!isCollapsed && (
              <>
                <span className="sidebar-nav-item-text flex-1 truncate">{item?.label}</span>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`sidebar-nav-chevron transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}
                />
              </>
            )}
          </div>
          {!isCollapsed && (
            <div
              className={`sidebar-nav-group-content ${expanded ? 'sidebar-nav-group-content-open' : ''}`}
            >
              <div className="min-h-0 space-y-0.5">
              {item.children.map((child) => {
                if (child?.children?.length) {
                  return (
                    <div key={child?.label} className="sidebar-nav-group pl-2 mt-1">
                      <div className="sidebar-nav-item sidebar-nav-group-header" style={{ paddingLeft: paddingLeft + 12 }}>
                        <Icon name={child?.icon} size={18} />
                        <span className="sidebar-nav-item-text">{child?.label}</span>
                      </div>
                      <div className="ml-6 mt-1 space-y-0.5">
                        {child.children.map((subChild) => {
                          const subActive = isActive(subChild?.path);
                          return (
                            <div
                              key={subChild?.path}
                              className={`sidebar-nav-item ${subActive ? 'active' : ''}`}
                              onClick={() => handleNavigation(subChild?.path)}
                              style={{ paddingLeft: paddingLeft + 24 }}
                            >
                              <Icon name={subChild?.icon} size={16} />
                              <span className="sidebar-nav-item-text">{subChild?.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                const childActive = isActive(child?.path);
                return (
                  <div
                    key={child?.path || child?.label}
                    className={`sidebar-nav-item ${childActive ? 'active' : ''}`}
                    onClick={() => handleNavigation(child?.path)}
                    style={{ paddingLeft: paddingLeft + 12 }}
                  >
                    <Icon name={child?.icon} size={18} />
                    <span className="sidebar-nav-item-text">{child?.label}</span>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={item?.path}
        className={`sidebar-nav-item ${active ? 'active' : ''}`}
        onClick={() => handleNavigation(item?.path)}
        style={{ paddingLeft }}
        title={itemTitle}
      >
        <Icon name={item?.icon} size={20} />
        {!isCollapsed && (
          <>
            <span className="sidebar-nav-item-text flex-1 truncate">{item?.label}</span>
            {item?.badge != null && (
              <span className="sidebar-nav-badge">{item.badge}</span>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className={`mobile-menu-button ${isMobileOpen ? 'sidebar-open' : ''}`}
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label={isMobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {isMobileOpen && (
        <div className="sidebar-overlay animate-fade-in" onClick={() => setIsMobileOpen(false)} aria-hidden="true" />
      )}
      <aside
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'animate-slide-in' : 'max-lg:hidden'}`}
        role="navigation"
        aria-label="Menu principal"
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Icon name="Wind" size={24} color="#FFFFFF" />
          </div>
          {!isCollapsed && (
            <div className="ml-3 min-w-0 leading-tight">
              <div className="text-base font-semibold text-foreground truncate">
                {t('sidebar.appName')}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                v{__APP_VERSION__}
              </div>
            </div>
          )}
        </div>

        <div className="sidebar-nav-scroll">
          <nav className="sidebar-nav">
            {visibleNavigationItems?.map((item) => renderNavItem(item))}
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
